/**
 * Registry of live NiiVue instances, keyed by pane id.
 *
 * Two things need to reach across panes and cannot live in React state:
 *  - crosshair/zoom linking, which NiiVue implements by handing each instance a
 *    list of its peers (`broadcastTo`);
 *  - ribbon actions (reset view, auto contrast, snapshot) that must act on
 *    whatever is on screen right now, from an action handler with no props.
 *
 * `Niivue` is imported as a type only, so this module stays free of the WebGL
 * bundle — it is safe to import from the panels.
 */

import type { Niivue } from "@niivue/niivue";

const instances = new Map<string, Niivue>();

/** Panes call this once their canvas has attached. */
export function registerInstance(id: string, nv: Niivue): void {
  instances.set(id, nv);
  applyLinkTopology();
}

export function unregisterInstance(id: string): void {
  instances.delete(id);
  applyLinkTopology();
}

export function getInstance(id: string): Niivue | undefined {
  return instances.get(id);
}

export function listInstances(): Niivue[] {
  return Array.from(instances.values());
}

/** Runs `fn` on every live instance, swallowing per-instance failures. */
export function forEachInstance(fn: (nv: Niivue) => void): void {
  for (const nv of instances.values()) {
    try {
      fn(nv);
    } catch (error) {
      console.warn("[viewer] instance action failed", error);
    }
  }
}

let linked = true;

export function setLinked(value: boolean): void {
  linked = value;
  applyLinkTopology();
}

export function isLinked(): boolean {
  return linked;
}

/**
 * Rewires every instance to broadcast to (or ignore) its peers.
 *
 * Called after any registration change because a pane that mounts late would
 * otherwise never be told about the panes that were already on screen.
 */
export function applyLinkTopology(): void {
  const all = Array.from(instances.values());
  for (const nv of all) {
    const peers = linked ? all.filter((other) => other !== nv) : [];
    try {
      nv.broadcastTo(peers, { "2d": true, "3d": true });
    } catch (error) {
      console.warn("[viewer] failed to update link topology", error);
    }
  }
}
